import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import api from '../services/api';
import { safeHtml } from '../utils/sanitizeHtml';

function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('mailboxes');
  const [mailboxes, setMailboxes] = useState([]);
  const [aiProviders, setAiProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [companySignatures, setCompanySignatures] = useState([]);
  const [autoresponders, setAutoresponders] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMailbox, setShowAddMailbox] = useState(false);
  const [editingMailbox, setEditingMailbox] = useState(null);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [showAddSignature, setShowAddSignature] = useState(false);
  const [showAutoresponderModal, setShowAutoresponderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingSignature, setEditingSignature] = useState(null);
  const [selectedMailboxForAutoresponder, setSelectedMailboxForAutoresponder] = useState(null);
  const [autoresponderConfig, setAutoresponderConfig] = useState(null);
  
  // Global sync context
  const { startSync } = useSync();
  const [newSignature, setNewSignature] = useState({
    name: '',
    htmlContent: '',
    plainTextContent: '',
    isDefault: false,
    isCompanySignature: false
  });
  const [newMailbox, setNewMailbox] = useState({
    emailAddress: '',
    displayName: '',
    protocol: 1,
    imapHost: '',
    imapPort: 993,
    smtpHost: '',
    smtpPort: 587,
    username: '',
    password: '',
    useSsl: true
  });
  const [newProvider, setNewProvider] = useState({
    providerTypeId: 1,
    name: '',
    apiKey: '',
    modelName: '',
    endpoint: '',
    maxTokens: 4096,
    temperature: 0.7,
    isDefault: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mailboxRes, aiRes, typesRes, sigRes, companySigRes, roleRes] = await Promise.allSettled([
        api.get('/mailboxes'),
        api.get('/settings/ai-providers'),
        api.get('/settings/ai-provider-types'),
        api.get('/signatures/personal'),
        api.get('/signatures/company'),
        api.get('/roles/my-role')
      ]);
      
      setMailboxes(mailboxRes.status === 'fulfilled' ? mailboxRes.value.data || [] : []);
      setAiProviders(aiRes.status === 'fulfilled' ? aiRes.value.data || [] : []);
      setProviderTypes(typesRes.status === 'fulfilled' ? typesRes.value.data || [] : [
        { id: 1, name: 'Gemini', description: 'Google Gemini AI', defaultModel: 'gemini-2.0-flash' },
        { id: 2, name: 'OpenAI', description: 'OpenAI GPT Models', defaultModel: 'gpt-4o' },
        { id: 3, name: 'AzureOpenAI', description: 'Azure OpenAI Service', defaultModel: 'gpt-4o' },
        { id: 4, name: 'Ollama', description: 'Local Ollama Models', defaultModel: 'llama3.2' }
      ]);
      setSignatures(sigRes.status === 'fulfilled' ? sigRes.value.data || [] : []);
      setCompanySignatures(companySigRes.status === 'fulfilled' ? companySigRes.value.data || [] : []);
      setMyRole(roleRes.status === 'fulfilled' ? roleRes.value.data : { roleName: 'User', permissionLevel: 10 });
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMailbox = async (e) => {
    e.preventDefault();
    try {
      await api.post('/mailboxes', newMailbox);
      setShowAddMailbox(false);
      setNewMailbox({
        emailAddress: '',
        displayName: '',
        protocol: 1,
        imapHost: '',
        imapPort: 993,
        smtpHost: '',
        smtpPort: 587,
        username: '',
        password: '',
        useSsl: true
      });
      loadData();
    } catch (error) {
      console.error('Failed to add mailbox:', error);
      // Surface real error details — a generic "Please check your settings"
      // alert hid issues like 401 (stale token), 400 (validation), and 502
      // (upstream mailcow/SMTP unreachable). We now show the HTTP status,
      // server-supplied message, and the JSON body to make support easier.
      const status = error?.response?.status;
      const data = error?.response?.data;
      const serverMsg =
        (data && (data.message || data.error || data.title || data.detail)) ||
        '';
      const bodyExtract = (() => {
        if (!data) return '';
        if (typeof data === 'string') return data.slice(0, 300);
        try { return JSON.stringify(data).slice(0, 300); } catch { return ''; }
      })();
      const parts = [];
      if (status) parts.push(`HTTP ${status}`);
      if (serverMsg) parts.push(serverMsg);
      else if (bodyExtract) parts.push(bodyExtract);
      else if (error?.message) parts.push(error.message);
      alert(`Failed to add mailbox.\n\n${parts.join('\n') || 'Unknown error.'}`);
    }
  };

  const handleUpdateMailbox = async (e) => {
    e.preventDefault();
    try {
      // Only send the password when the user actually typed a new one — the
      // backend keeps the existing encrypted password when this is blank, so
      // editing host/port/display name never wipes the saved credential.
      const payload = {
        displayName: editingMailbox.displayName,
        imapHost: editingMailbox.imapHost,
        imapPort: editingMailbox.imapPort,
        smtpHost: editingMailbox.smtpHost,
        smtpPort: editingMailbox.smtpPort,
        useSsl: editingMailbox.useSsl,
      };
      if (editingMailbox.newPassword) payload.password = editingMailbox.newPassword;
      await api.put(`/mailboxes/${editingMailbox.mailboxId}`, payload);
      setEditingMailbox(null);
      loadData();
    } catch (error) {
      console.error('Failed to update mailbox:', error);
      const status = error?.response?.status;
      const data = error?.response?.data;
      const serverMsg =
        (data && (data.message || data.error || data.title || data.detail)) || '';
      const parts = [];
      if (status) parts.push(`HTTP ${status}`);
      if (serverMsg) parts.push(serverMsg);
      else if (error?.message) parts.push(error.message);
      alert(`Failed to update mailbox.\n\n${parts.join('\n') || 'Unknown error.'}`);
    }
  };

  const handleTestConnection = async (mailboxId) => {
    try {
      await api.post(`/mailboxes/${mailboxId}/test`);
      alert('Connection successful!');
    } catch (error) {
      alert('Connection failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSyncMailbox = async (mailboxId) => {
    const mailbox = mailboxes.find(m => m.mailboxId === mailboxId);
    try {
      // Show progress bar INSTANTLY before API call
      await startSync(mailboxId, mailbox?.emailAddress);
      
      // Then trigger the actual sync
      await api.post(`/mailboxes/${mailboxId}/sync`);
      // Progress bar updates via SignalR
    } catch (error) {
      alert('Sync failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteMailbox = async (mailboxId) => {
    if (window.confirm('Are you sure you want to delete this mailbox?')) {
      try {
        await api.delete(`/mailboxes/${mailboxId}`);
        loadData();
      } catch (error) {
        console.error('Failed to delete mailbox:', error);
      }
    }
  };

  // AI Provider handlers
  const handleAddProvider = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/ai-providers', newProvider);
      setShowAddProvider(false);
      setNewProvider({
        providerTypeId: 1,
        name: '',
        apiKey: '',
        modelName: '',
        endpoint: '',
        maxTokens: 4096,
        temperature: 0.7,
        isDefault: false
      });
      loadData();
    } catch (error) {
      console.error('Failed to add AI provider:', error);
      alert('Failed to add AI provider: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateProvider = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/settings/ai-providers/${editingProvider.providerId}`, {
        name: editingProvider.name,
        apiKey: editingProvider.newApiKey || undefined,
        modelName: editingProvider.modelName,
        endpoint: editingProvider.endpoint,
        maxTokens: editingProvider.maxTokens,
        temperature: editingProvider.temperature,
        isDefault: editingProvider.isDefault,
        isActive: editingProvider.isActive
      });
      setEditingProvider(null);
      loadData();
    } catch (error) {
      console.error('Failed to update AI provider:', error);
      alert('Failed to update AI provider: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (window.confirm('Are you sure you want to delete this AI provider?')) {
      try {
        await api.delete(`/settings/ai-providers/${providerId}`);
        loadData();
      } catch (error) {
        console.error('Failed to delete AI provider:', error);
      }
    }
  };

  const handleSetDefaultProvider = async (providerId) => {
    try {
      await api.post(`/settings/ai-providers/${providerId}/set-default`);
      loadData();
    } catch (error) {
      alert('Failed to set default: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleTestProvider = async (providerId) => {
    try {
      const response = await api.post(`/settings/ai-providers/${providerId}/test`);
      alert(response.data.success ? 'Provider test successful!' : 'Test failed: ' + response.data.message);
    } catch (error) {
      alert('Test failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const getProviderIcon = (providerType) => {
    const icons = {
      'Gemini': { bg: 'linear-gradient(135deg, #4285f4, #34a853)', text: 'G' },
      'OpenAI': { bg: 'linear-gradient(135deg, #10a37f, #1a7f5a)', text: 'O' },
      'AzureOpenAI': { bg: 'linear-gradient(135deg, #0078d4, #00bcf2)', text: 'A' },
      'Ollama': { bg: 'linear-gradient(135deg, #6b7280, #374151)', text: 'L' },
      'Claude': { bg: 'linear-gradient(135deg, #d97706, #f59e0b)', text: 'C' },
      'DeepSeek': { bg: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', text: 'D' },
      'Grok': { bg: 'linear-gradient(135deg, #000000, #333333)', text: 'X' }
    };
    return icons[providerType] || icons['Gemini'];
  };

  // Signature handlers
  const handleAddSignature = async (e) => {
    e.preventDefault();
    try {
      const endpoint = newSignature.isCompanySignature ? '/signatures/company' : '/signatures/personal';
      await api.post(endpoint, newSignature);
      setShowAddSignature(false);
      setNewSignature({ name: '', htmlContent: '', plainTextContent: '', isDefault: false, isCompanySignature: false });
      loadData();
    } catch (error) {
      console.error('Failed to add signature:', error);
      alert('Failed to add signature: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateSignature = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingSignature.isCompanySignature 
        ? `/signatures/company/${editingSignature.signatureID}`
        : `/signatures/personal/${editingSignature.signatureID}`;
      await api.put(endpoint, editingSignature);
      setEditingSignature(null);
      loadData();
    } catch (error) {
      console.error('Failed to update signature:', error);
      alert('Failed to update signature');
    }
  };

  const handleDeleteSignature = async (signatureId, isCompany) => {
    if (window.confirm('Are you sure you want to delete this signature?')) {
      try {
        const endpoint = isCompany ? `/signatures/company/${signatureId}` : `/signatures/personal/${signatureId}`;
        await api.delete(endpoint);
        loadData();
      } catch (error) {
        console.error('Failed to delete signature:', error);
      }
    }
  };

  const handleSetDefaultSignature = async (signatureId) => {
    try {
      await api.post(`/signatures/personal/${signatureId}/set-default`);
      loadData();
    } catch (error) {
      alert('Failed to set default: ' + (error.response?.data?.message || error.message));
    }
  };

  // Autoresponder handlers
  const openAutoresponderModal = async (mailbox) => {
    setSelectedMailboxForAutoresponder(mailbox);
    try {
      const response = await api.get(`/autoresponders/mailbox/${mailbox.mailboxId}`);
      setAutoresponderConfig(response.data);
    } catch (error) {
      setAutoresponderConfig({
        mailboxID: mailbox.mailboxId,
        isEnabled: false,
        internalEnabled: true,
        externalEnabled: true,
        sendOncePerSender: true,
        sendOncePerDays: 7
      });
    }
    setShowAutoresponderModal(true);
  };

  const handleSaveAutoresponder = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/autoresponders/mailbox/${selectedMailboxForAutoresponder.mailboxId}`, autoresponderConfig);
      setShowAutoresponderModal(false);
      setSelectedMailboxForAutoresponder(null);
      setAutoresponderConfig(null);
      loadData();
    } catch (error) {
      console.error('Failed to save autoresponder:', error);
      alert('Failed to save autoresponder: ' + (error.response?.data?.message || error.message));
    }
  };

  // Check if user can manage company settings
  const canManageCompany = myRole?.permissionLevel >= 80; // Admin or Boss

  const tabs = [
    { id: 'mailboxes', label: 'Email Accounts', icon: '📧' },
    { id: 'signatures', label: 'Signatures', icon: '✍️' },
    { id: 'autoresponders', label: 'Autoresponders', icon: '🔄' },
    { id: 'ai', label: 'AI Providers', icon: '✨' },
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' }
  ];

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Settings Sidebar */}
      <div style={{ width: '240px', background: 'var(--secondary-bg)', borderRight: '1px solid var(--border-color)', padding: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>Settings</h2>
        <nav>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '12px',
                background: activeTab === tab.id ? 'var(--accent-primary)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                cursor: 'pointer',
                marginBottom: '4px',
                fontSize: '14px',
                textAlign: 'left'
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Settings Content */}
      <div style={{ flex: 1, padding: '24px', overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Mailboxes Tab */}
            {activeTab === 'mailboxes' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: 600 }}>Email Accounts</h3>
                  <button className="btn btn-primary" onClick={() => setShowAddMailbox(true)}>
                    + Add Account
                  </button>
                </div>

                {mailboxes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📧</div>
                    <div className="empty-state-title">No email accounts</div>
                    <div className="empty-state-text">Add your first email account to get started.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {mailboxes.map(mailbox => (
                      <div key={mailbox.mailboxId} style={{
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '20px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                              {mailbox.displayName || mailbox.emailAddress}
                            </h4>
                            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{mailbox.emailAddress}</p>
                            <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '8px' }}>
                              Protocol: {['', 'IMAP', 'Exchange', 'Graph'][mailbox.protocol]} | 
                              Last sync: {mailbox.lastSyncUtc ? new Date(mailbox.lastSyncUtc).toLocaleString() : 'Never'}
                            </p>
                            {mailbox.syncStatus === 3 && (
                              <p style={{ color: 'var(--error)', fontSize: '13px', marginTop: '4px' }}>
                                Error: {mailbox.syncErrorMessage}
                              </p>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn btn-secondary" onClick={() => handleTestConnection(mailbox.mailboxId)}>
                              Test
                            </button>
                            <button className="btn btn-secondary" onClick={() => handleSyncMailbox(mailbox.mailboxId)}>
                              Sync
                            </button>
                            <button className="btn btn-secondary" onClick={() => setEditingMailbox({ ...mailbox, newPassword: '' })}>
                              Edit
                            </button>
                            <button className="btn btn-secondary" onClick={() => handleDeleteMailbox(mailbox.mailboxId)}>
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Signatures Tab */}
            {activeTab === 'signatures' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: 600 }}>Email Signatures</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
                      Create personal signatures and view company-wide signatures.
                    </p>
                  </div>
                  <button className="btn btn-primary" onClick={() => setShowAddSignature(true)}>
                    + Add Signature
                  </button>
                </div>

                {/* Personal Signatures */}
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>👤</span> My Signatures
                  </h4>
                  {signatures.length === 0 ? (
                    <div className="empty-state" style={{ padding: '32px' }}>
                      <div className="empty-state-icon">✍️</div>
                      <div className="empty-state-title">No personal signatures</div>
                      <div className="empty-state-text">Create your first signature to appear at the bottom of your emails.</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {signatures.map(sig => (
                        <div key={sig.signatureID} style={{
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '16px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <h5 style={{ fontSize: '15px', fontWeight: 600 }}>{sig.name}</h5>
                                {sig.isDefault && (
                                  <span style={{ background: 'var(--success)', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}>
                                    Default
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                fontSize: '13px', 
                                color: 'var(--text-secondary)', 
                                background: 'var(--tertiary-bg)',
                                padding: '12px',
                                borderRadius: '8px',
                                maxHeight: '100px',
                                overflow: 'auto'
                              }} dangerouslySetInnerHTML={safeHtml(sig.htmlContent || sig.plainTextContent || '<em>No content</em>')} />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                              {!sig.isDefault && (
                                <button className="btn btn-secondary" onClick={() => handleSetDefaultSignature(sig.signatureID)} title="Set as default">
                                  ⭐
                                </button>
                              )}
                              <button className="btn btn-secondary" onClick={() => setEditingSignature(sig)}>
                                Edit
                              </button>
                              <button className="btn btn-secondary" onClick={() => handleDeleteSignature(sig.signatureID, false)}>
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Company Signatures */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🏢</span> Company Signatures
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 400 }}>(Mandatory - added to all outgoing emails)</span>
                    </h4>
                    {canManageCompany && (
                      <button className="btn btn-secondary" onClick={() => { setNewSignature({ ...newSignature, isCompanySignature: true }); setShowAddSignature(true); }}>
                        + Add Company Signature
                      </button>
                    )}
                  </div>
                  {companySignatures.length === 0 ? (
                    <div style={{ padding: '24px', background: 'var(--tertiary-bg)', borderRadius: '12px', textAlign: 'center' }}>
                      <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                        No company signatures configured. {canManageCompany ? 'Add a company-wide signature for legal disclaimers or branding.' : 'Contact your administrator to add company signatures.'}
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {companySignatures.map(sig => (
                        <div key={sig.signatureID} style={{
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '16px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <h5 style={{ fontSize: '15px', fontWeight: 600 }}>{sig.name}</h5>
                                <span style={{ 
                                  background: 'var(--accent-primary)', 
                                  color: 'white', 
                                  fontSize: '10px', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  COMPANY
                                </span>
                                {!sig.isActive && (
                                  <span style={{ background: 'var(--warning)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>
                                    DISABLED
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                fontSize: '13px', 
                                color: 'var(--text-secondary)', 
                                background: 'var(--tertiary-bg)',
                                padding: '12px',
                                borderRadius: '8px',
                                maxHeight: '100px',
                                overflow: 'auto'
                              }} dangerouslySetInnerHTML={safeHtml(sig.htmlContent || sig.plainTextContent || '<em>No content</em>')} />
                            </div>
                            {canManageCompany && (
                              <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                                <button className="btn btn-secondary" onClick={() => setEditingSignature({ ...sig, isCompanySignature: true })}>
                                  Edit
                                </button>
                                <button className="btn btn-secondary" onClick={() => handleDeleteSignature(sig.signatureID, true)}>
                                  🗑️
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Autoresponders Tab */}
            {activeTab === 'autoresponders' && (
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: 600 }}>Autoresponders</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
                    Configure automatic replies for your email accounts. Set different messages for internal (colleagues) and external senders.
                  </p>
                </div>

                {mailboxes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📧</div>
                    <div className="empty-state-title">No email accounts</div>
                    <div className="empty-state-text">Add an email account first to configure autoresponders.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {mailboxes.map(mailbox => (
                      <div key={mailbox.mailboxId} style={{
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '20px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                              {mailbox.displayName || mailbox.emailAddress}
                            </h4>
                            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{mailbox.emailAddress}</p>
                          </div>
                          <button className="btn btn-primary" onClick={() => openAutoresponderModal(mailbox)}>
                            Configure Autoresponder
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: '24px', padding: '20px', background: 'var(--tertiary-bg)', borderRadius: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>ℹ️ About Autoresponders</h4>
                  <ul style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, paddingLeft: '20px' }}>
                    <li><strong>Internal:</strong> Replies sent to people within your organization (same email domain)</li>
                    <li><strong>External:</strong> Replies sent to people outside your organization</li>
                    <li><strong>Schedule:</strong> Set start/end dates for vacation modes</li>
                    <li><strong>Once per sender:</strong> Only send one auto-reply per person during the active period</li>
                  </ul>
                </div>
              </div>
            )}

            {/* AI Providers Tab */}
            {activeTab === 'ai' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: 600 }}>AI Providers</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
                      Configure AI providers for email assistance features like summaries, smart replies, and more.
                    </p>
                  </div>
                  <button className="btn btn-primary" onClick={() => setShowAddProvider(true)}>
                    + Add Provider
                  </button>
                </div>
                
                {aiProviders.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">🤖</div>
                    <div className="empty-state-title">No AI providers configured</div>
                    <div className="empty-state-text">Add an AI provider to enable smart email features.</div>
                    <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowAddProvider(true)}>
                      Add Provider
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {aiProviders.map(provider => {
                      const icon = getProviderIcon(provider.providerType);
                      return (
                        <div key={provider.providerId} style={{
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '20px',
                          opacity: provider.isActive ? 1 : 0.6
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                              <div style={{ 
                                width: '48px', 
                                height: '48px', 
                                background: icon.bg, 
                                borderRadius: '10px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                fontSize: '24px',
                                fontWeight: 700,
                                color: 'white'
                              }}>
                                {icon.text}
                              </div>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <h4 style={{ fontSize: '16px', fontWeight: 600 }}>{provider.name}</h4>
                                  {provider.isDefault && (
                                    <span style={{ 
                                      background: 'var(--success)', 
                                      color: 'white', 
                                      fontSize: '11px', 
                                      padding: '2px 8px', 
                                      borderRadius: '4px',
                                      fontWeight: 500
                                    }}>
                                      Default
                                    </span>
                                  )}
                                  {!provider.isActive && (
                                    <span style={{ 
                                      background: 'var(--warning)', 
                                      color: 'white', 
                                      fontSize: '11px', 
                                      padding: '2px 8px', 
                                      borderRadius: '4px' 
                                    }}>
                                      Disabled
                                    </span>
                                  )}
                                </div>
                                <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '4px' }}>
                                  {provider.providerType} • Model: {provider.modelName || 'Default'}
                                </p>
                                <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                                  API Key: {provider.hasApiKey ? '••••••••••••••••' : 'Not configured'}
                                  {provider.endpoint && ` • Endpoint: ${provider.endpoint}`}
                                </p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {!provider.isDefault && (
                                <button 
                                  className="btn btn-secondary" 
                                  onClick={() => handleSetDefaultProvider(provider.providerId)}
                                  title="Set as default"
                                >
                                  ⭐
                                </button>
                              )}
                              <button 
                                className="btn btn-secondary" 
                                onClick={() => handleTestProvider(provider.providerId)}
                              >
                                Test
                              </button>
                              <button 
                                className="btn btn-secondary" 
                                onClick={() => setEditingProvider({...provider, newApiKey: ''})}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn btn-secondary" 
                                onClick={() => handleDeleteProvider(provider.providerId)}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: '24px', padding: '20px', background: 'var(--tertiary-bg)', borderRadius: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Provider Support</h4>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    Currently supported: Google Gemini (fully functional). 
                    OpenAI, Azure OpenAI, and Ollama support coming soon.
                  </p>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>Profile</h3>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
                    <div className="sender-avatar" style={{ width: '80px', height: '80px', fontSize: '32px' }}>
                      {user?.displayName?.[0] || user?.email?.[0] || 'U'}
                    </div>
                    <div>
                      <h4 style={{ fontSize: '18px', fontWeight: 600 }}>{user?.displayName || 'User'}</h4>
                      <p style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Name</label>
                    <input type="text" className="form-input" defaultValue={user?.displayName || ''} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={user?.email || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Timezone</label>
                    <select className="form-input" defaultValue={user?.timeZoneId || 'UTC'}>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                    </select>
                  </div>
                  <button className="btn btn-primary" style={{ marginTop: '8px' }}>Save Changes</button>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>Preferences</h3>
                
                {/* AI Features */}
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>AI Features</h4>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked />
                      <span>Enable AI email summaries</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '28px', marginTop: '4px' }}>
                      Generate one-line and bullet point summaries for emails
                    </p>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked />
                      <span>Show smart reply suggestions</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '28px', marginTop: '4px' }}>
                      AI-generated reply options based on email content
                    </p>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" />
                      <span>Learn my writing voice</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '28px', marginTop: '4px' }}>
                      Analyze your sent emails to match your writing style in drafts
                    </p>
                  </div>
                </div>

                {/* Email Triage Settings */}
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Email Triage & Organization</h4>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked />
                      <span>Auto-triage incoming emails</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '28px', marginTop: '4px' }}>
                      Analyze and assign priority scores to incoming emails
                    </p>
                  </div>
                  
                  <div style={{ marginLeft: '28px', marginTop: '12px', padding: '12px', background: 'var(--tertiary-bg)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 500, marginBottom: '12px' }}>Folder Auto-Organization</p>
                    <div className="form-group" style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                        <input type="radio" name="triageMode" value="suggest" defaultChecked />
                        <span>Suggest folder moves (I'll confirm each one)</span>
                      </label>
                    </div>
                    <div className="form-group" style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                        <input type="radio" name="triageMode" value="auto" />
                        <span>Auto-move with high confidence (85%+ confidence)</span>
                      </label>
                    </div>
                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                        <input type="radio" name="triageMode" value="off" />
                        <span>Don't suggest folder moves</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Company-Wide Settings */}
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Company-Wide Features</h4>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" />
                      <span>Allow company-wide AI learning</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '28px', marginTop: '4px' }}>
                      Share anonymized email patterns to improve AI suggestions company-wide
                    </p>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked />
                      <span>Use company email templates</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '28px', marginTop: '4px' }}>
                      Access shared templates from your organization
                    </p>
                  </div>
                  <button className="btn btn-primary" style={{ marginTop: '8px' }}>Save Preferences</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Mailbox Modal */}
      {showAddMailbox && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>Add Email Account</h3>
            <form onSubmit={handleAddMailbox}>
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  className="form-input"
                  value={newMailbox.emailAddress}
                  onChange={(e) => setNewMailbox({ ...newMailbox, emailAddress: e.target.value, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newMailbox.displayName}
                  onChange={(e) => setNewMailbox({ ...newMailbox, displayName: e.target.value })}
                />
              </div>
              
              {/* IMAP - Incoming Mail */}
              <div style={{ 
                background: 'var(--tertiary-bg)', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '12px' 
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  📥 <strong>IMAP</strong> - Incoming mail server (to receive emails)
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">IMAP Host *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newMailbox.imapHost}
                      onChange={(e) => setNewMailbox({ ...newMailbox, imapHost: e.target.value })}
                      placeholder="imap.yourprovider.com"
                      required
                    />
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Common: imap.gmail.com, outlook.office365.com, imap.mail.yahoo.com
                    </div>
                  </div>
                  <div className="form-group" style={{ width: '80px', marginBottom: 0 }}>
                    <label className="form-label">Port</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newMailbox.imapPort}
                      onChange={(e) => setNewMailbox({ ...newMailbox, imapPort: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              
              {/* SMTP - Outgoing Mail */}
              <div style={{ 
                background: 'var(--tertiary-bg)', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '12px' 
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  📤 <strong>SMTP</strong> - Outgoing mail server (to send emails)
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">SMTP Host *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newMailbox.smtpHost}
                      onChange={(e) => setNewMailbox({ ...newMailbox, smtpHost: e.target.value })}
                      placeholder="smtp.yourprovider.com"
                      required
                    />
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Common: smtp.gmail.com, smtp.office365.com, smtp.mail.yahoo.com
                    </div>
                  </div>
                  <div className="form-group" style={{ width: '80px', marginBottom: 0 }}>
                    <label className="form-label">Port</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newMailbox.smtpPort}
                      onChange={(e) => setNewMailbox({ ...newMailbox, smtpPort: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={newMailbox.password}
                  onChange={(e) => setNewMailbox({ ...newMailbox, password: e.target.value })}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newMailbox.useSsl}
                    onChange={(e) => setNewMailbox({ ...newMailbox, useSsl: e.target.checked })}
                  />
                  <span>Use SSL/TLS</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddMailbox(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Add Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Mailbox Modal — change password / server settings on an existing
          account. The email address / username are fixed (changing them means
          a different account, which is "Add Account"). Leaving Password blank
          keeps the current saved credential. */}
      {editingMailbox && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '4px', fontSize: '18px', fontWeight: 600 }}>Edit Email Account</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
              {editingMailbox.emailAddress}
            </p>
            <form onSubmit={handleUpdateMailbox}>
              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingMailbox.displayName || ''}
                  onChange={(e) => setEditingMailbox({ ...editingMailbox, displayName: e.target.value })}
                />
              </div>

              {/* IMAP - Incoming Mail */}
              <div style={{
                background: 'var(--tertiary-bg)',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  📥 <strong>IMAP</strong> - Incoming mail server
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">IMAP Host</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editingMailbox.imapHost || ''}
                      onChange={(e) => setEditingMailbox({ ...editingMailbox, imapHost: e.target.value })}
                    />
                  </div>
                  <div className="form-group" style={{ width: '80px', marginBottom: 0 }}>
                    <label className="form-label">Port</label>
                    <input
                      type="number"
                      className="form-input"
                      value={editingMailbox.imapPort}
                      onChange={(e) => setEditingMailbox({ ...editingMailbox, imapPort: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>

              {/* SMTP - Outgoing Mail */}
              <div style={{
                background: 'var(--tertiary-bg)',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  📤 <strong>SMTP</strong> - Outgoing mail server
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">SMTP Host</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editingMailbox.smtpHost || ''}
                      onChange={(e) => setEditingMailbox({ ...editingMailbox, smtpHost: e.target.value })}
                    />
                  </div>
                  <div className="form-group" style={{ width: '80px', marginBottom: 0 }}>
                    <label className="form-label">Port</label>
                    <input
                      type="number"
                      className="form-input"
                      value={editingMailbox.smtpPort}
                      onChange={(e) => setEditingMailbox({ ...editingMailbox, smtpPort: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={editingMailbox.newPassword || ''}
                  onChange={(e) => setEditingMailbox({ ...editingMailbox, newPassword: e.target.value })}
                  placeholder="Leave blank to keep current password"
                  autoComplete="new-password"
                />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Enter a new password only if it changed on the mail server.
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingMailbox.useSsl}
                    onChange={(e) => setEditingMailbox({ ...editingMailbox, useSsl: e.target.checked })}
                  />
                  <span>Use SSL/TLS</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingMailbox(null)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add AI Provider Modal */}
      {showAddProvider && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>Add AI Provider</h3>
            <form onSubmit={handleAddProvider}>
              <div className="form-group">
                <label className="form-label">Provider Type *</label>
                <select
                  className="form-input"
                  value={newProvider.providerTypeId}
                  onChange={(e) => {
                    const typeId = parseInt(e.target.value);
                    const type = providerTypes.find(t => t.id === typeId);
                    setNewProvider({ 
                      ...newProvider, 
                      providerTypeId: typeId,
                      modelName: type?.defaultModel || ''
                    });
                  }}
                >
                  {providerTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name} - {type.description}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newProvider.name}
                  onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                  placeholder="My Gemini API"
                  required
                />
              </div>
              {(() => {
                const selectedType = providerTypes.find(t => t.id === newProvider.providerTypeId);
                return (
                  <>
                    {selectedType?.apiKeyUrl && (
                      <div style={{ 
                        padding: '12px', 
                        background: 'var(--tertiary-bg)', 
                        borderRadius: '8px', 
                        marginBottom: '16px',
                        fontSize: '13px'
                      }}>
                        {selectedType.hasFreeTier && (
                          <span style={{ 
                            background: 'var(--success)', 
                            color: 'white', 
                            padding: '2px 6px', 
                            borderRadius: '4px', 
                            fontSize: '11px',
                            marginRight: '8px'
                          }}>
                            FREE TIER
                          </span>
                        )}
                        <a 
                          href={selectedType.apiKeyUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'var(--accent-primary)' }}
                        >
                          Get your {selectedType.name} API key →
                        </a>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="form-group">
                <label className="form-label">API Key {providerTypes.find(t => t.id === newProvider.providerTypeId)?.name === 'Ollama' ? '(optional)' : '*'}</label>
                <input
                  type="password"
                  className="form-input"
                  value={newProvider.apiKey}
                  onChange={(e) => setNewProvider({ ...newProvider, apiKey: e.target.value })}
                  placeholder={providerTypes.find(t => t.id === newProvider.providerTypeId)?.name === 'Ollama' ? 'Not required for Ollama' : 'Enter your API key'}
                  required={providerTypes.find(t => t.id === newProvider.providerTypeId)?.name !== 'Ollama'}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Model</label>
                {(() => {
                  const selectedType = providerTypes.find(t => t.id === newProvider.providerTypeId);
                  const models = selectedType?.availableModels || [];
                  return models.length > 0 ? (
                    <select
                      className="form-input"
                      value={newProvider.modelName}
                      onChange={(e) => setNewProvider({ ...newProvider, modelName: e.target.value })}
                    >
                      {models.map(model => (
                        <option key={model.modelId} value={model.modelId}>
                          {model.name} ({model.tier}) - {model.description}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={newProvider.modelName}
                      onChange={(e) => setNewProvider({ ...newProvider, modelName: e.target.value })}
                      placeholder="e.g., gemini-2.0-flash or gpt-4o"
                    />
                  );
                })()}
              </div>
              {(newProvider.providerTypeId === 3 || newProvider.providerTypeId === 4) && (
                <div className="form-group">
                  <label className="form-label">Endpoint URL *</label>
                  <input
                    type="url"
                    className="form-input"
                    value={newProvider.endpoint}
                    onChange={(e) => setNewProvider({ ...newProvider, endpoint: e.target.value })}
                    placeholder={newProvider.providerTypeId === 4 ? 'http://localhost:11434' : 'https://your-resource.openai.azure.com/'}
                    required={newProvider.providerTypeId === 3 || newProvider.providerTypeId === 4}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Max Tokens</label>
                  <input
                    type="number"
                    className="form-input"
                    value={newProvider.maxTokens}
                    onChange={(e) => setNewProvider({ ...newProvider, maxTokens: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Temperature</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    className="form-input"
                    value={newProvider.temperature}
                    onChange={(e) => setNewProvider({ ...newProvider, temperature: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newProvider.isDefault}
                    onChange={(e) => setNewProvider({ ...newProvider, isDefault: e.target.checked })}
                  />
                  <span>Set as default provider</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddProvider(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Add Provider
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit AI Provider Modal */}
      {editingProvider && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>Edit AI Provider</h3>
            <form onSubmit={handleUpdateProvider}>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingProvider.name}
                  onChange={(e) => setEditingProvider({ ...editingProvider, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">API Key {editingProvider.hasApiKey ? '(leave blank to keep current)' : '*'}</label>
                <input
                  type="password"
                  className="form-input"
                  value={editingProvider.newApiKey || ''}
                  onChange={(e) => setEditingProvider({ ...editingProvider, newApiKey: e.target.value })}
                  placeholder={editingProvider.hasApiKey ? '••••••••••••••••' : 'Enter your API key'}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Model Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingProvider.modelName || ''}
                  onChange={(e) => setEditingProvider({ ...editingProvider, modelName: e.target.value })}
                />
              </div>
              {(editingProvider.providerTypeId === 3 || editingProvider.providerTypeId === 4) && (
                <div className="form-group">
                  <label className="form-label">Endpoint URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={editingProvider.endpoint || ''}
                    onChange={(e) => setEditingProvider({ ...editingProvider, endpoint: e.target.value })}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Max Tokens</label>
                  <input
                    type="number"
                    className="form-input"
                    value={editingProvider.maxTokens}
                    onChange={(e) => setEditingProvider({ ...editingProvider, maxTokens: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Temperature</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    className="form-input"
                    value={editingProvider.temperature}
                    onChange={(e) => setEditingProvider({ ...editingProvider, temperature: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingProvider.isActive}
                    onChange={(e) => setEditingProvider({ ...editingProvider, isActive: e.target.checked })}
                  />
                  <span>Provider is active</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingProvider(null)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Signature Modal */}
      {(showAddSignature || editingSignature) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>
              {editingSignature ? 'Edit Signature' : 'Add Signature'}
            </h3>
            <form onSubmit={editingSignature ? handleUpdateSignature : handleAddSignature}>
              <div className="form-group">
                <label className="form-label">Signature Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingSignature ? editingSignature.name : newSignature.name}
                  onChange={(e) => editingSignature 
                    ? setEditingSignature({ ...editingSignature, name: e.target.value })
                    : setNewSignature({ ...newSignature, name: e.target.value })}
                  placeholder="e.g., Work Signature, Personal"
                  required
                />
              </div>

              {canManageCompany && !editingSignature && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newSignature.isCompanySignature}
                      onChange={(e) => setNewSignature({ ...newSignature, isCompanySignature: e.target.checked })}
                    />
                    <span>This is a company-wide signature (mandatory for all users)</span>
                  </label>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Signature Content (HTML) *</label>
                <textarea
                  className="form-input"
                  rows={8}
                  value={editingSignature ? editingSignature.htmlContent : newSignature.htmlContent}
                  onChange={(e) => editingSignature 
                    ? setEditingSignature({ ...editingSignature, htmlContent: e.target.value })
                    : setNewSignature({ ...newSignature, htmlContent: e.target.value })}
                  placeholder={`<p>Best regards,</p>
<p><strong>Your Name</strong><br/>
Your Title<br/>
Your Company</p>
<p>📧 email@company.com | 📱 +1 234 567 8900</p>`}
                  required
                />
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Use HTML for formatting. Common tags: &lt;p&gt;, &lt;br/&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a href="..."&gt;
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Plain Text Version (optional)</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={editingSignature ? editingSignature.plainTextContent : newSignature.plainTextContent}
                  onChange={(e) => editingSignature 
                    ? setEditingSignature({ ...editingSignature, plainTextContent: e.target.value })
                    : setNewSignature({ ...newSignature, plainTextContent: e.target.value })}
                  placeholder="Best regards,
Your Name
Your Title
email@company.com"
                />
              </div>

              {!newSignature.isCompanySignature && !editingSignature?.isCompanySignature && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingSignature ? editingSignature.isDefault : newSignature.isDefault}
                      onChange={(e) => editingSignature 
                        ? setEditingSignature({ ...editingSignature, isDefault: e.target.checked })
                        : setNewSignature({ ...newSignature, isDefault: e.target.checked })}
                    />
                    <span>Set as my default signature</span>
                  </label>
                </div>
              )}

              {/* Preview */}
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label">Preview</label>
                <div style={{ 
                  background: 'var(--tertiary-bg)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }} dangerouslySetInnerHTML={safeHtml(
                  (editingSignature ? editingSignature.htmlContent : newSignature.htmlContent) || '<em>Your signature preview will appear here</em>'
                )} />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowAddSignature(false); setEditingSignature(null); }} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  {editingSignature ? 'Save Changes' : 'Add Signature'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Autoresponder Modal */}
      {showAutoresponderModal && autoresponderConfig && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: 600 }}>
              Configure Autoresponder
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
              {selectedMailboxForAutoresponder?.emailAddress}
            </p>
            
            <form onSubmit={handleSaveAutoresponder}>
              {/* Enable Toggle */}
              <div className="form-group" style={{ 
                background: autoresponderConfig.isEnabled ? 'rgba(16, 185, 129, 0.1)' : 'var(--tertiary-bg)',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={autoresponderConfig.isEnabled}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, isEnabled: e.target.checked })}
                  />
                  <span style={{ fontWeight: 600 }}>Enable Autoresponder</span>
                </label>
              </div>

              {/* Schedule */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Start Date (optional)</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={autoresponderConfig.startDateUtc ? new Date(autoresponderConfig.startDateUtc).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, startDateUtc: e.target.value || null })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">End Date (optional)</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={autoresponderConfig.endDateUtc ? new Date(autoresponderConfig.endDateUtc).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, endDateUtc: e.target.value || null })}
                  />
                </div>
              </div>

              {/* Internal Response */}
              <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--tertiary-bg)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600 }}>📥 Internal Response (Same Domain)</h4>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={autoresponderConfig.internalEnabled}
                      onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, internalEnabled: e.target.checked })}
                    />
                    <span style={{ fontSize: '13px' }}>Enabled</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-input"
                    value={autoresponderConfig.internalSubject || ''}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, internalSubject: e.target.value })}
                    placeholder="Re: [Auto] I'm currently out of office"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={autoresponderConfig.internalMessage || ''}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, internalMessage: e.target.value })}
                    placeholder="Hi, I'm currently out of the office with limited access to email. For urgent matters, please contact..."
                  />
                </div>
              </div>

              {/* External Response */}
              <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--tertiary-bg)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600 }}>📤 External Response (Different Domain)</h4>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={autoresponderConfig.externalEnabled}
                      onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, externalEnabled: e.target.checked })}
                    />
                    <span style={{ fontSize: '13px' }}>Enabled</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-input"
                    value={autoresponderConfig.externalSubject || ''}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, externalSubject: e.target.value })}
                    placeholder="Re: [Auto] Out of Office Reply"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={autoresponderConfig.externalMessage || ''}
                    onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, externalMessage: e.target.value })}
                    placeholder="Thank you for your email. I am currently out of the office and will respond when I return..."
                  />
                </div>
              </div>

              {/* Behavior Settings */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>⚙️ Behavior Settings</h4>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={autoresponderConfig.sendOncePerSender}
                      onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, sendOncePerSender: e.target.checked })}
                    />
                    <span>Only send one auto-reply per sender</span>
                  </label>
                </div>
                {autoresponderConfig.sendOncePerSender && (
                  <div className="form-group" style={{ marginLeft: '28px' }}>
                    <label className="form-label">Reset after (days)</label>
                    <input
                      type="number"
                      className="form-input"
                      style={{ width: '100px' }}
                      value={autoresponderConfig.sendOncePerDays}
                      onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, sendOncePerDays: parseInt(e.target.value) || 7 })}
                      min={1}
                      max={365}
                    />
                  </div>
                )}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={autoresponderConfig.onlyRespondToContacts}
                      onChange={(e) => setAutoresponderConfig({ ...autoresponderConfig, onlyRespondToContacts: e.target.checked })}
                    />
                    <span>Only respond to known contacts</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowAutoresponderModal(false); setSelectedMailboxForAutoresponder(null); }} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save Autoresponder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
